import { Box, Typography } from '@mui/material';

import { StatusChip, tokens } from '../../../theme-next';

interface HeroCardProps {
  regPlate?: string | null;
  vehicleSummary?: string | null;
  docsReceived: number;
  docsExpected: number;
  alertsCount: number;
  aiConfidence: number | null; // 0-100
  reviewStatus: 'none' | 'pending_god_review' | 'god_approved' | 'god_rejected';
  // ── Mode édition (chip "Pièces à déposer" → /form-cgia?registrationId=X) ──
  /** RegistrationID si on est en redepôt — change l'overline + titre. */
  editingRegistrationId?: number | null;
  /** Nombre de pièces à redéposer côté agence (openRequests.length). */
  requestedCount?: number;
}

const Kpi = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 90 }}>
    <Typography variant="overline" sx={{ color: '#C7C7C5' }}>
      {label}
    </Typography>
    {/* key={String(value)} force un remount → rejoue l'animation pop à chaque
        changement de valeur, pour que l'œil voit la progression. */}
    <Typography
      key={String(value)}
      className="sk-pop-in"
      variant="h2"
      sx={{ color: tokens.color.white, lineHeight: 1, fontSize: '32px', fontWeight: 800 }}
    >
      {value}
    </Typography>
  </Box>
);

export const HeroCard = ({
  regPlate,
  vehicleSummary,
  docsReceived,
  docsExpected,
  alertsCount,
  aiConfidence,
  reviewStatus,
  editingRegistrationId = null,
  requestedCount = 0,
}: HeroCardProps) => {
  const plate = regPlate ?? '—';
  const reliabilityLabel = aiConfidence != null ? `${aiConfidence}%` : '—';
  const inEditMode = editingRegistrationId !== null;

  const reviewLabel: Record<HeroCardProps['reviewStatus'], string> = {
    none: 'En cours de préparation',
    pending_god_review: `${alertsCount} alertes à traiter`,
    god_approved: 'Validé',
    god_rejected: 'À corriger',
  };

  // Overline / titre branchent sur le mode édition pour signaler clairement
  // qu'on n'est pas en train de créer un nouveau dossier mais d'en compléter
  // un existant (réponse à une demande de redepôt côté Skipcar).
  const overlineText = inEditMode
    ? `REDEPÔT DE PIÈCES · DEMANDE #${editingRegistrationId}`
    : 'CTVO — Changement de titulaire VO';
  const overlineColor = inEditMode ? tokens.color.orange : tokens.color.yellow;

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.dark,
        color: tokens.color.white,
        borderRadius: `${tokens.radius['3xl']}px`,
        padding: '24px 28px',
        display: 'flex',
        alignItems: 'center',
        gap: '32px',
        boxShadow: tokens.shadow.lg,
        flexWrap: 'wrap',
        // Liseré orange en mode édition pour renforcer le signal visuel
        ...(inEditMode && {
          borderTop: `3px solid ${tokens.color.orange}`,
        }),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography variant="overline" sx={{ color: overlineColor, fontWeight: 700 }}>
          {overlineText}
        </Typography>
        <Typography
          sx={{
            fontSize: '48px',
            fontWeight: 900,
            letterSpacing: '0.04em',
            lineHeight: 1,
            color: tokens.color.white,
            marginTop: '4px',
          }}
        >
          {plate}
        </Typography>
        {vehicleSummary && (
          <Typography variant="body2" sx={{ color: '#C7C7C5', marginTop: '6px' }}>
            {vehicleSummary}
          </Typography>
        )}
        {inEditMode && requestedCount > 0 && (
          <Typography
            sx={{
              color: tokens.color.orange,
              marginTop: '12px',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {requestedCount} pièce{requestedCount > 1 ? 's' : ''} à redéposer — les autres
            sont déjà validées.
          </Typography>
        )}
      </Box>

      <Kpi label="Documents" value={`${docsReceived}/${docsExpected}`} />
      <Kpi label="Alertes" value={alertsCount} />
      <Kpi label="Fiabilité" value={reliabilityLabel} />

      <Box sx={{ minWidth: 180, textAlign: 'right' }}>
        <StatusChip
          status={
            reviewStatus === 'god_approved'
              ? 'delivered'
              : reviewStatus === 'god_rejected'
                ? 'blocked'
                : alertsCount > 0
                  ? 'toVerify'
                  : 'pending'
          }
        />
        <Typography variant="caption" sx={{ color: '#C7C7C5', display: 'block', marginTop: '6px' }}>
          {reviewLabel[reviewStatus]}
        </Typography>
      </Box>
    </Box>
  );
};
